/**
 * What an empty list says, written once.
 *
 * The same "nothing assigned to you" appears on the dashboard and on the
 * Projects summary, and two copies of a joke drift apart the first time
 * somebody improves one of them.
 *
 * Light on purpose. An empty list is good news more often than not, so it can
 * afford to sound pleased - but it is still read by somebody at work, so a
 * raised eyebrow rather than a punchline.
 */
export const EMPTY_COPY = {
  assigned: {
    title: 'Your plate is clear',
    hint: 'Nothing is assigned to you. Savour it - it rarely lasts.',
  },
  overdue: {
    title: 'Nothing is late',
    hint: 'Every deadline is still in front of you, which is where deadlines belong.',
  },
  activity: {
    title: 'All quiet',
    hint: 'Nothing has happened here yet. Give it a minute.',
  },
  activityFiltered: {
    title: 'Nothing matches',
    hint: 'Nobody did that, then. Try a wider date range or fewer filters.',
  },
  inbox: {
    title: 'All caught up',
    hint: 'Nobody needs anything from you right now. Suspicious, but enjoy it.',
  },
  inboxFiltered: {
    title: 'Nothing unread',
    hint: 'You have read every single thing. The other tab has the rest.',
  },
  comments: {
    title: 'No comments yet',
    hint: 'Somebody has to go first. It might as well be you.',
  },
  /*
   * Plainer than the rest of these on purpose.
   *
   * The subscriptions screens are read by whoever handles the money, and
   * they asked for labels rather than turns of phrase. The rotating quote
   * stays - that is the standing rule for an empty screen - but the line
   * above it says what to do and nothing else.
   */
  subscriptions: {
    title: 'No subscriptions yet',
    hint: 'Add one to track its cost, seats and renewals.',
  },
  charges: {
    title: 'No payments recorded',
    hint: 'Record a payment to see it here.',
  },
  seats: {
    title: 'No seats assigned',
    hint: 'Assign seats to see who has access.',
  },
  archive: {
    title: 'Nothing archived',
    hint: 'The board is holding on to everything for now.',
  },
  history: {
    title: 'A fresh start',
    hint: 'Nothing has happened to this task yet. Every legend starts somewhere.',
  },
  dashboard: {
    title: 'A blank canvas',
    hint: 'Every widget is hidden. "Add widget" brings them back - pick the ones worth looking at.',
  },
  throughput: {
    title: 'No activity yet',
    hint: 'Open a task or finish one and this chart gets to show off.',
  },
  stages: {
    title: 'No tasks on any board yet',
    hint: 'The bars are ready. They are just waiting for some work.',
  },
  /**
   * An empty board column: one short line and no quote.
   *
   * The exception to the rule, and deliberately. A board shows several
   * columns side by side, so a quote in every empty one would put three or
   * four of them on screen at once - noise where there should be a lull.
   */
  column: 'Empty, for now',
} as const
